import { useParams } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";
import { CasinoCard } from "@/components/CasinoCard";
import { RiskNotice } from "@/components/RiskNotice";
import { FAQ, buildFAQSchema } from "@/components/FAQ";
import { findPayment } from "@/data/payments";
import { listCasinos } from "@/data/casinos";
import { NotFoundPage } from "./NotFoundPage";

type Variant = "default" | "casino" | "unlicensed";

export function PaymentMethodPage({ variant = "default" }: { variant?: Variant }) {
  const { method = "" } = useParams<{ method: string }>();
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const pm = findPayment(method);
  if (!pm) return <NotFoundPage />;

  const casinos = listCasinos({
    [pm.name.toLowerCase() as "swish" | "trustly" | "zimpler" | "brite" | "bankid"]:
      ["swish", "trustly", "zimpler", "brite", "bankid"].includes(pm.name.toLowerCase())
        ? true
        : undefined,
    crypto: pm.type === "crypto" ? true : undefined,
    paymentMethod: pm.id.replace(/^pm-/, ""),
    noSwedishLicense: variant === "unlicensed" ? true : undefined,
    swedishLicense: variant === "casino" ? true : undefined,
  }).sort(
    (a, b) =>
      Number(b.priority_brand) - Number(a.priority_brand) ||
      b.overall_rating - a.overall_rating,
  );

  const title =
    variant === "unlicensed"
      ? isSv
        ? `${pm.name} casino utan svensk licens`
        : `${pm.name} casinos without Swedish license`
      : variant === "casino"
        ? (isSv ? `${pm.name} — casinon med svensk licens` : `${pm.name} — Swedish-licensed casinos`)
      : isSv
        ? `Casino med ${pm.name} 2026 — komplett guide`
        : `Casinos with ${pm.name} 2026 — complete guide`;

  const desc = `${title}. ${isSv ? pm.description_sv : pm.description_en}`;
  const sections = isSv ? pm.long_sections_sv : pm.long_sections_en;
  const pros = isSv ? pm.pros_sv : pm.pros_en;
  const cons = isSv ? pm.cons_sv : pm.cons_en;

  const faq = [
    {
      question_sv: `Fungerar ${pm.name} på casinon med svensk licens?`,
      question_en: `Does ${pm.name} work at Swedish-licensed casinos?`,
      answer_sv: pm.works_with_swedish_license
        ? `Ja, ${pm.name} används på flera svenska casinon med licens.`
        : `Nej, ${pm.name} används främst utanför svensk licens.`,
      answer_en: pm.works_with_swedish_license
        ? `Yes, ${pm.name} is used at several Swedish-licensed casinos.`
        : `No, ${pm.name} is mainly used outside Swedish license.`,
    },
    {
      question_sv: `Krävs BankID för ${pm.name}?`,
      question_en: `Is BankID required for ${pm.name}?`,
      answer_sv: pm.requires_bankid
        ? `Ja, ${pm.name} använder BankID för signering.`
        : `Nej, ${pm.name} kräver inte BankID.`,
      answer_en: pm.requires_bankid
        ? `Yes, ${pm.name} uses BankID for signing.`
        : `No, ${pm.name} does not require BankID.`,
    },
    {
      question_sv: `Kostar det något att använda ${pm.name}?`,
      question_en: `Is there a fee to use ${pm.name}?`,
      answer_sv: `Hos casinon är ${pm.name} oftast avgiftsfritt för spelaren. Kontrollera alltid kassasidan innan du bekräftar transaktionen.`,
      answer_en: `At casinos ${pm.name} is typically free for the player. Always verify on the cashier page before confirming the transaction.`,
    },
    {
      question_sv: `Hur snabbt kan jag göra uttag med ${pm.name}?`,
      question_en: `How fast can I withdraw with ${pm.name}?`,
      answer_sv: pm.typical_speed
        ? `Typisk handläggningstid är ${pm.typical_speed.toLowerCase()}.`
        : `Handläggningstiden beror på operatören och din bank.`,
      answer_en: pm.typical_speed
        ? `Typical processing time is ${pm.typical_speed.toLowerCase()}.`
        : `Processing time depends on the operator and your bank.`,
    },
  ];

  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} jsonLd={buildFAQSchema(faq, lang)} />
      <Breadcrumbs
        items={[
          { label: t("nav.home"), href: prefix },
          {
            label: isSv ? "Betalningsmetoder" : "Payment methods",
            href: `${prefix}/${isSv ? "betalningsmetoder" : "payment-methods"}`,
          },
          { label: pm.name },
        ]}
      />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-3 max-w-3xl text-lg text-ink-700">{desc}</p>

      <section className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-4">
        <Stat label={isSv ? "Insättning" : "Deposit"} value={pm.supports_deposit ? "✓" : "✗"} />
        <Stat label={isSv ? "Uttag" : "Withdrawal"} value={pm.supports_withdrawal ? "✓" : "✗"} />
        <Stat label="BankID" value={pm.requires_bankid ? "✓" : "✗"} />
        <Stat
          label={isSv ? "Hastighet" : "Speed"}
          value={pm.typical_speed ?? t("label.unknown")}
        />
      </section>

      {variant === "unlicensed" && (
        <div className="mt-4">
          <RiskNotice variant="unlicensed" />
        </div>
      )}

      {sections && sections.length > 0 && (
        <div className="mt-10 space-y-8">
          {sections.map((s, i) => (
            <section key={i}>
              <h2 className="text-2xl font-bold text-ink-900">{s.heading}</h2>
              <p className="mt-3 text-base leading-relaxed text-ink-700">{s.body}</p>
            </section>
          ))}
        </div>
      )}

      {((pros && pros.length > 0) || (cons && cons.length > 0)) && (
        <section className="mt-10 grid gap-4 md:grid-cols-2">
          {pros && pros.length > 0 && (
            <div className="card p-5">
              <h3 className="font-semibold text-emerald-700">
                {isSv ? "Fördelar" : "Pros"}
              </h3>
              <ul className="mt-2 space-y-1 text-sm text-ink-700">
                {pros.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <span aria-hidden className="mt-0.5 text-emerald-600">
                      ✓
                    </span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {cons && cons.length > 0 && (
            <div className="card p-5">
              <h3 className="font-semibold text-rose-700">
                {isSv ? "Nackdelar" : "Cons"}
              </h3>
              <ul className="mt-2 space-y-1 text-sm text-ink-700">
                {cons.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <span aria-hidden className="mt-0.5 text-rose-600">
                      ✗
                    </span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <h2 className="mt-12 text-2xl font-bold">
        {isSv ? `Casinon med ${pm.name}` : `Casinos with ${pm.name}`}
      </h2>
      <div className="mt-3 grid gap-4">
        {casinos.length === 0 ? (
          <p className="card p-6 text-ink-600">{t("list.empty")}</p>
        ) : (
          casinos.map((c, i) => (
            <CasinoCard
              key={c.id}
              casino={c}
              rank={i + 1}
              source={`payment-${pm.name.toLowerCase()}`}
            />
          ))
        )}
      </div>

      <div className="mt-10">
        <FAQ items={faq} />
      </div>

      <InternalLinks context="payment" />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-4">
      <p className="text-xs uppercase tracking-wide text-ink-500">{label}</p>
      <p className="mt-1 text-lg font-bold text-ink-900">{value}</p>
    </div>
  );
}
